export const useGlobalStore = defineStore('globalStore', {
    state: () => ({
        pageType: 'page',
        uri: '',
        navigation: [],
        globalSets: [],
        microsites: [],

        siteDetails: {},
        digistormSettings: {},

        newsListingUrl: '',
        newsletterListingUrl: '',
        eventsListingUrl: '',

        themes: [],
        globalTheme: '',

        fontWeightCss: '',
        themeCss: [],

        bannerHasBorder: false,
        showHamburgerMenu: false,
    }),
    getters: {
        locale(): string {
            const localeData: string = get(this.siteDetails, 'locale', 'en-AU')

            // if the locale is en-US then just return en since that is the us default for dayjs
            if (localeData === 'en-US') {
                return 'en'
            }

            return localeData
        },

        // country code is just the 2 letter code for the country where locale includes language and country
        // au vs en-au, this is just the au part
        countryCode(): string {
            // transform from locale
            return toLower(last(split(get(this.siteDetails, 'locale'), '-')))
        },

        appId(): string {
            return get(this.siteDetails, 'appId', '')
        },

        homeUri(): string {
            const { isMicrosite, microsite } = storeToRefs(useMicrositeStore())

            if (isMicrosite.value) {
                return `/${microsite.value.uri}`
            }

            return '/'
        },

        getNavigation() {
            const { isMicrosite, navigation } = storeToRefs(useMicrositeStore())
            if (isMicrosite.value) {
                return navigation.value
            }
            return this.navigation
        },

        getGlobalSetField: (state) => (globalSetType: string, fieldHandle: string, fallback = false) => {
            const { isMicrosite, microsite } = storeToRefs(useMicrositeStore())
            if (isMicrosite.value) {
                const field = get(microsite.value, fieldHandle, null)

                if (!isEmpty(field) || !fallback) {
                    return field
                }
            }

            const globalSet = find(state.globalSets, { __typename: globalSetType })
            return globalSet?.[fieldHandle]
        },

        getGlobalTheme() {
            const { isMicrosite, globalTheme: micrositeGlobalTheme } = storeToRefs(useMicrositeStore())
            if (isMicrosite.value) {
                return !isEmpty(micrositeGlobalTheme.value) ? micrositeGlobalTheme.value : this.globalTheme
            }
            return this.globalTheme
        },

        websiteTier(): string {
            return get(this.digistormSettings, 'websiteTier', 'foundation')
        },

        placeholderImage() {
            return this.getGlobalSetField('ds_Logos_GlobalSet', 'logos_PlaceholderImage', true) || []
        },

        headerLogo() {
            return this.getGlobalSetField('ds_Logos_GlobalSet', 'logos_HeaderLogo', true) || []
        },

        placeholderImageSize(): string {
            return get(this.digistormSettings, 'placeholderImageSize', 'medium')
        },

        placeholderBackgroundColor(): string {
            return get(this.digistormSettings, 'backgroundColorOverride', '') as string
        },

    },
    actions: {
        async loadSite() {
            const { cmsQuery } = useGraphQL()

            const { data: siteData } = await cmsQuery({
                uri: 'site-data',
                key: 'siteData',
            })

            this.init(siteData.value?.data)
        },

        // load settings for the site and various other settings
        init(data) {
            this.navigation = get(data, 'entries', [])
            this.globalSets = get(data, 'globalSets', [])

            this.themes = get(data, 'colorThemes', [])
            this.globalTheme = get(find(this.themes, { defaultTheme: true }), 'id')

            this.digistormSettings = JSON.parse(get(data, 'digistormComponents', '{}'))
            this.siteDetails = JSON.parse(get(data, 'siteDetails', '{}'))

            this.globalSets = get(data, 'globalSets', [])
            this.microsites = get(data, 'microsites', [])

            // load colors for error pages
            this.initColorTheme()

            // load font weight variables
            this.initFontWeights()
        },

        initColorTheme() {
            // sort themes so default theme is first to fix css hierarchy issues
            const sortedThemes = sortBy(this.themes, (theme) => !theme.defaultTheme)

            each(sortedThemes, (theme) => {

                let colourCss = ''

                const themeData = JSON.parse(theme?.themeData ?? '{}')

                // add brand information
                const brandData = get(themeData, 'brand', {})
                const baseColor = get(themeData, 'baseColor', '')
                const brandOverlay = get(themeData, 'brand.overlay', '')
                const overlayColor = brandOverlay || baseColor || brandData[700] || ''

                colourCss += `
[data-theme="${theme.id}"] {
`
                forIn(brandData, (value, key) => {
                    colourCss += `    --color-brand-${key}: ${value};\n`
                })

                // create overlay colours
                colourCss += `    --color-brand-overlay-5: ${overlayColor}0d;\n`
                colourCss += `    --color-brand-overlay-10: ${overlayColor}1a;\n`
                colourCss += `    --color-brand-overlay-20: ${overlayColor}33;\n`
                colourCss += `    --color-brand-overlay-30: ${overlayColor}4d;\n`
                colourCss += `    --color-brand-overlay-40: ${overlayColor}66;\n`
                colourCss += `    --color-brand-overlay-50: ${overlayColor}80;\n`
                colourCss += `    --color-brand-overlay-60: ${overlayColor}99;\n`
                colourCss += `    --color-brand-overlay-70: ${overlayColor}b2;\n`
                colourCss += `    --color-brand-overlay-80: ${overlayColor}cc;\n`
                colourCss += `    --color-brand-overlay-90: ${overlayColor}e5;\n`

                // add accent
                colourCss += `    --color-brand-accent: ${themeData.accentColor};\n`

                colourCss += '}\n'

                // loop through all default values
                for (const key of ['base', 'color-invert', 'color-subtle']) {
                    let mode = key
                    switch (key) {
                        case 'color-subtle':
                            mode = 'brand'
                            break
                        case 'color-invert':
                            mode = 'brand-invert'
                            break
                        default:
                            mode = key
                            break
                    }

                    colourCss += `
    [data-theme="${theme.id}"][data-mode="${mode}"],
    [data-theme="${theme.id}"] [data-mode="${mode}"] {\n`

                    const themeVariables = get(themeData, key, {})

                    forIn(themeVariables, (value, cssVar) => {
                        colourCss += `    ${cssVar}: ${value};\n`
                    })

                    colourCss += '}\n'
                }

                this.themeCss.push({
                    id: theme.id,
                    css: colourCss,
                })

            })
        },

        initFontWeights() {
            const defaultWeights = this.digistormSettings?.fontWeights.default
            const boldWeights = this.digistormSettings?.fontWeights.richTextBold

            this.fontWeightCss = `html body {
    --font-weight-heading: ${defaultWeights.heading};
    --font-weight-subheading: ${defaultWeights.subheading};
    --font-weight-regular: ${defaultWeights.body};
    --font-weight-button: ${defaultWeights.button};

    --font-weight-heading-strong: ${boldWeights.heading};
    --font-weight-subheading-strong: ${boldWeights.subheading};
    --font-weight-ux-strong: ${boldWeights.body};
}`
        }
    },
})
